import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Locate, Loader2, ExternalLink, MapPin } from "lucide-react";
import { toast } from "sonner";
import { reverseGeocode } from "@/lib/api";
import { getCurrentPosition, isLatLng } from "@/components/maps/geo";

const PIN = L.divIcon({ className: "", html: '<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;background:#047857;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35)"></div>', iconSize: [26, 26], iconAnchor: [13, 26] });
const INDIA = [22.5, 79];

function Recenter({ pos }) {
  const map = useMap();
  useEffect(() => { if (pos) map.setView(pos, Math.max(map.getZoom(), 16)); }, [pos?.[0], pos?.[1]]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}
function ClickToMove({ onMove }) {
  useMapEvents({ click: (e) => onMove(e.latlng.lat, e.latlng.lng) });
  return null;
}

// Draggable pin; only reports a change when the user drags/taps/uses GPS. Parent decides when to save.
export default function LocationPinPicker({ lat, lng, onChange, onClear, fallbackCenter, testid = "pin-picker" }) {
  const has = isLatLng(lat, lng);
  const pos = has ? [Number(lat), Number(lng)] : null;
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!has) { setLabel(""); return; }
    const t = setTimeout(() => reverseGeocode(lat, lng).then((r) => setLabel([r?.locality, r?.pincode].filter(Boolean).join(" · "))).catch(() => setLabel("")), 600);
    return () => clearTimeout(t);
  }, [lat, lng]); // eslint-disable-line react-hooks/exhaustive-deps

  const move = (a, b) => onChange(+a.toFixed(6), +b.toFixed(6));
  const useGps = async () => {
    setBusy(true);
    try {
      const p = await getCurrentPosition();
      move(p.lat, p.lng);
      if (p.accuracy > 150) toast.warning(`Location lagbhag ${Math.round(p.accuracy)} m tak sahi hai — pin khiska kar theek karein.`);
      else toast.success("Current location mil gayi. Pin check karke Save karein.");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={testid}>
      <div className="relative rounded-2xl overflow-hidden border border-slate-200 h-60 sm:h-72 z-0">
        <MapContainer center={pos || fallbackCenter || INDIA} zoom={pos ? 16 : fallbackCenter ? 13 : 4} scrollWheelZoom={false} className="w-full h-full">
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {pos && <Marker position={pos} icon={PIN} draggable eventHandlers={{ dragend: (e) => { const p = e.target.getLatLng(); move(p.lat, p.lng); } }} />}
          <ClickToMove onMove={move} />
          <Recenter pos={pos} />
        </MapContainer>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button type="button" onClick={useGps} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 text-emerald-800 px-3 py-1.5 text-xs font-semibold hover:bg-emerald-50 min-h-[36px] disabled:opacity-60" data-testid={`${testid}-gps`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Locate className="w-3.5 h-3.5" />} Use my current location
        </button>
        {has && onClear && <button type="button" onClick={() => { if (window.confirm("Map pin hata dein? Save dabane par hi hatega. Aapka profile PIN code / browsing PIN nahi badlega.")) onClear(); }} className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 text-rose-700 px-3 py-1.5 text-xs font-semibold hover:bg-rose-50 min-h-[36px]" data-testid={`${testid}-clear`}>Pin hatayein</button>}
        {has && <a href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-emerald-400 min-h-[36px]" data-testid={`${testid}-google`}><ExternalLink className="w-3.5 h-3.5" /> Google Maps pe check karein</a>}
      </div>
      <div className="mt-1.5 text-xs text-slate-500 flex items-center gap-1" data-testid={`${testid}-label`}>
        <MapPin className="w-3.5 h-3.5 text-emerald-700" />
        {has ? (label ? `Pin: ${label}` : "Pin set hai") : "Abhi koi pin set nahi — map pe tap karein ya current location use karein."}
      </div>
      <p className="text-[11px] text-slate-400 mt-0.5">Pin ko khiska kar ya map pe tap karke sahi jagah lagaayein. Save dabane par hi location save hogi.</p>
    </div>
  );
}
